import { useQuery } from '@tanstack/react-query';
import { Loader2, X } from 'lucide-react';
import { getNode } from '@/api/fleet';
import {
  formatHashrate,
  formatUptime,
  timeAgo,
  STATUS_BADGE,
  PLATFORM_META
} from '@/lib/format';

export function NodeDetailModal({
  nodeKey,
  onClose,
  onDecommission
}: {
  nodeKey: string;
  onClose: () => void;
  onDecommission: () => void;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['fleet-node', nodeKey],
    queryFn: () => getNode(nodeKey)
  });

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full max-w-2xl rounded-2xl border border-nurev-border bg-nurev-surface p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-bold text-nurev-text">Node detail</h2>
          <button onClick={onClose} className="text-nurev-muted hover:text-nurev-text">
            <X className="h-5 w-5" />
          </button>
        </div>

        {isLoading || !data ? (
          <div className="flex items-center gap-3 py-8 text-nurev-muted">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="space-y-5">
            <div>
              <div className="mb-1 text-xs font-medium uppercase tracking-wider text-nurev-muted">
                Node Key
              </div>
              <code className="block break-all rounded-lg border border-nurev-border bg-nurev-bg px-3 py-2 font-mono text-xs text-nurev-text">
                {data.nodeKey}
              </code>
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-[10px] font-medium uppercase tracking-wider text-nurev-muted">
                  Label
                </div>
                <div className="text-nurev-text">{data.label || '—'}</div>
              </div>
              <div>
                <div className="text-[10px] font-medium uppercase tracking-wider text-nurev-muted">
                  Hostname
                </div>
                <div className="text-nurev-text">{data.hostname || '—'}</div>
              </div>
              <div>
                <div className="text-[10px] font-medium uppercase tracking-wider text-nurev-muted">
                  Status
                </div>
                <span
                  className={`inline-block rounded px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider ${
                    STATUS_BADGE[data.status] || ''
                  }`}
                >
                  {data.status}
                </span>
              </div>
              <div>
                <div className="text-[10px] font-medium uppercase tracking-wider text-nurev-muted">
                  Platform
                </div>
                <div className="text-nurev-text">
                  {PLATFORM_META[data.platform]?.emoji}{' '}
                  {PLATFORM_META[data.platform]?.label || data.platform}
                </div>
              </div>
              <div>
                <div className="text-[10px] font-medium uppercase tracking-wider text-nurev-muted">
                  Group
                </div>
                <div className="text-nurev-text">{data.group}</div>
              </div>
              <div>
                <div className="text-[10px] font-medium uppercase tracking-wider text-nurev-muted">
                  Last seen
                </div>
                <div className="text-nurev-text">{timeAgo(data.lastHeartbeatAt)}</div>
              </div>
              <div>
                <div className="text-[10px] font-medium uppercase tracking-wider text-nurev-muted">
                  Hardware
                </div>
                <div className="text-nurev-text">
                  {data.specs.cpuCores} cores ·{' '}
                  {(data.specs.ramMb / 1024).toFixed(1)} GB RAM
                  {data.specs.gpu && ` · ${data.specs.gpu}`}
                </div>
              </div>
              <div>
                <div className="text-[10px] font-medium uppercase tracking-wider text-nurev-muted">
                  Uptime
                </div>
                <div className="text-nurev-text">
                  {formatUptime(data.stats.uptime)}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-lg border border-nurev-border bg-nurev-card p-3">
                <div className="text-[10px] font-medium uppercase tracking-wider text-nurev-muted">
                  Speed
                </div>
                <div className="font-mono text-lg font-bold text-emerald-300">
                  {formatHashrate(data.stats.hashrate)}
                </div>
              </div>
              <div className="rounded-lg border border-nurev-border bg-nurev-card p-3">
                <div className="text-[10px] font-medium uppercase tracking-wider text-nurev-muted">
                  Shares (✓ / ✗)
                </div>
                <div className="text-lg font-bold text-nurev-text">
                  {data.stats.sharesAccepted.toLocaleString()} /{' '}
                  <span className="text-nurev-muted">
                    {data.stats.sharesRejected.toLocaleString()}
                  </span>
                </div>
              </div>
              <div className="rounded-lg border border-nurev-border bg-nurev-card p-3">
                <div className="text-[10px] font-medium uppercase tracking-wider text-nurev-muted">
                  CPU / Memory
                </div>
                <div className="text-lg font-bold text-nurev-text">
                  {data.stats.cpuUsage != null ? `${Math.round(data.stats.cpuUsage)}%` : '—'}
                  {' · '}
                  {data.stats.memUsageMb != null
                    ? `${(data.stats.memUsageMb / 1024).toFixed(1)} GB`
                    : '—'}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => {
                  if (
                    window.confirm(
                      'Decommission this node? It will stop contributing and be hidden from the list.'
                    )
                  ) {
                    onDecommission();
                  }
                }}
                className="btn-secondary text-red-300"
              >
                Decommission
              </button>
              <button onClick={onClose} className="btn-primary">
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
